import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import bcrypt from "bcryptjs";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function BcryptGenerator() {
  const [password, setPassword] = useState("");
  const [rounds, setRounds] = useState(10);
  const [hash, setHash] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Verifier state
  const [verifyPassword, setVerifyPassword] = useState("");
  const [verifyHash, setVerifyHash] = useState("");
  const [isMatch, setIsMatch] = useState<boolean | null>(null);

  const generateHash = () => {
    if (!password) return;
    setIsGenerating(true);
    // Use timeout to allow UI to update before blocking thread (since bcryptjs is sync by default unless async method is used properly, though it has async)
    setTimeout(() => {
      try {
        const salt = bcrypt.genSaltSync(rounds);
        const newHash = bcrypt.hashSync(password, salt);
        setHash(newHash);
      } catch (e) {
        console.error(e);
      } finally {
        setIsGenerating(false);
      }
    }, 10);
  };

  const handleCopy = () => {
    if (!hash) return;
    copyToClipboard(hash).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (!verifyPassword || !verifyHash) {
      setIsMatch(null);
      return;
    }
    try {
      const match = bcrypt.compareSync(verifyPassword, verifyHash);
      setIsMatch(match);
    } catch {
      setIsMatch(false); // Invalid hash format
    }
  }, [verifyPassword, verifyHash]);

  return (
    <ToolPageLayout
      title="Bcrypt Hash Generator & Verifier Online"
      description="Generate and verify Bcrypt hashes instantly with our secure online tool. Perfect for testing password hashing algorithms."
      h1="Bcrypt Generator"
      category="Security & Crypto"
      categoryPath="/security-crypto"
      howToUse={[
        "Enter your password and select the cost (rounds) to generate a Bcrypt hash.",
        "Click 'Generate Hash' and copy the resulting hash.",
        "Use the 'Verify Hash' section to check if a password matches a given Bcrypt hash."
      ]}
      faq={[
        { question: "What is Bcrypt?", answer: "Bcrypt is a password-hashing function designed to be slow, making brute-force attacks more difficult." },
        { question: "Is this tool secure?", answer: "Yes, this tool performs hashing locally in your browser. No data is sent to a server." }
      ]}
    >
      <div className="max-w-2xl mx-auto space-y-12">
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-slate-900 border-b border-slate-200 pb-2">Generate Bcrypt Hash</h2>
          <div className="grid md:grid-cols-4 gap-4 items-end">
            <div className="space-y-2 md:col-span-3">
              <Label>Plain Text Password</Label>
              <Input 
                type="text" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter text to hash..."
              />
            </div>
            <div className="space-y-2">
              <Label>Cost (Rounds)</Label>
              <Input 
                type="number" 
                min="4" 
                max="20" 
                value={rounds}
                onChange={(e) => setRounds(Number(e.target.value))}
              />
            </div>
          </div>
          
          <Button onClick={generateHash} disabled={!password || isGenerating} className="w-full">
            {isGenerating ? "Generating..." : "Generate Hash"}
          </Button>

          {hash && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
              <Label>Generated Hash</Label>
              <div className="flex gap-2">
                <Input value={hash} readOnly className="font-mono bg-white text-slate-900 break-all h-auto py-3" />
                <Button onClick={handleCopy} variant="outline" className="shrink-0" size="icon" aria-label="Copy bcrypt hash to clipboard">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {copied && <div className="text-xs text-green-600">Copied to clipboard!</div>}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <h2 className="text-xl font-bold text-slate-900 border-b border-slate-200 pb-2">Verify Hash</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Hash to verify</Label>
              <Input 
                type="text" 
                value={verifyHash}
                onChange={(e) => setVerifyHash(e.target.value)}
                placeholder="$2a$10$..."
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label>Password to test against</Label>
              <Input 
                type="text" 
                value={verifyPassword}
                onChange={(e) => setVerifyPassword(e.target.value)}
                placeholder="Enter password..."
              />
            </div>
          </div>

          {verifyHash && verifyPassword && (
            <div className={`p-4 rounded-lg border flex items-center gap-3 ${isMatch ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
              {isMatch ? <CheckCircle2 className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
              <span className="font-medium text-lg">
                {isMatch ? "Match!" : "Does Not Match"}
              </span>
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  );
}
